<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { moveStyle, styleRows, toggleStyle } from '@/settings/tagStyles'
import type { TagStyle } from '@/todos/hashtags'

// The allowed hashtag spellings in the order of preference (`User.tag_styles`): a checkbox allows one, the arrows order them.
const props = defineProps<{ modelValue: readonly TagStyle[]; disabled?: boolean }>()
const emit = defineEmits<{ 'update:modelValue': [styles: TagStyle[]] }>()

const { t } = useI18n()
const rows = computed(() => styleRows(props.modelValue))
const EXAMPLES: Record<TagStyle, string> = {
  plain: '#camelCase',
  brackets: '#[two words]',
  quotes: '#"two words"',
}
</script>

<template>
  <ul class="styles">
    <li v-for="(row, index) in rows" :key="row.style">
      <label>
        <input
          type="checkbox"
          :checked="row.allowed"
          :disabled="disabled || (row.allowed && modelValue.length <= 1)"
          @change="emit('update:modelValue', toggleStyle(modelValue, row.style))"
        />
        <span class="name">{{ t(`settings.tagStyles.${row.style}`) }}</span>
        <code>{{ EXAMPLES[row.style] }}</code>
      </label>
      <span v-if="row.allowed" class="move">
        <button
          type="button"
          class="ui-btn"
          :disabled="disabled || index === 0"
          :aria-label="t('settings.tagStyles.up')"
          @click="emit('update:modelValue', moveStyle(modelValue, row.style, -1))"
        >
          ▲
        </button>
        <button
          type="button"
          class="ui-btn"
          :disabled="disabled || index === modelValue.length - 1"
          :aria-label="t('settings.tagStyles.down')"
          @click="emit('update:modelValue', moveStyle(modelValue, row.style, 1))"
        >
          ▼
        </button>
      </span>
    </li>
  </ul>
</template>

<style scoped>
.styles {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
li {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  flex-wrap: wrap;
}
label {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  min-width: 0;
}
.move {
  display: flex;
  gap: 4px;
}
</style>
